import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { LineIcon } from '../../ui/icons/LineIcon.js';
import { useDevice } from '../../../contexts/DeviceContext.js';
import { cn } from '../../../lib/utils.js';

const NativeSelect = forwardRef(
  ({
    className,
    onValueChange,
    variants,
    scale = "default",
    isValid = false,
    disabled = false,
    id,
    ...props
  }, ref) => {
    const { isDesktop } = useDevice();
    const selectRef = useRef(null);
    const [selected, setSelected] = useState({
      firstLine: "",
      secondLine: ""
    });
    const setRefs = (node) => {
      selectRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };
    const handUnderLineChange = (e) => {
      if (variants === "underLine") {
        const selectText = e.target.selectedOptions[0]?.innerText ?? "";
        const [firstLine, secondLine] = selectText.split("\n");
        setSelected({ firstLine, secondLine });
      }
    };
    useEffect(() => {
      if (variants === "underLine") {
        const selectText = selectRef.current?.selectedOptions[0]?.innerText ?? "";
        const [firstLine, secondLine] = selectText.split("\n");
        setSelected({ firstLine: firstLine ?? "", secondLine: secondLine ?? "" });
      }
    }, []);
    const rootRef = useRef(null);
    const [title, setTitle] = useState("");
    useEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      const Field = root.closest('[data-slot="field"]');
      const labelField = Field?.querySelector('[data-slot="field-label"]');
      const labelText = labelField?.querySelector(".label-text");
      if (!labelText) return;
      const clone = labelText.cloneNode(true);
      clone.querySelectorAll('[role="group"]').forEach((el) => el.remove());
      const text = clone.textContent?.trim() ?? "";
      console.log(clone);
      setTitle(text);
    }, []);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "group/native-select relative",
          (variants === "email" || variants === "full") && "w-[100%]"
        ),
        "data-slot": "native-select-wrapper",
        ref: rootRef,
        children: [
          variants === "underLine" && /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                isDesktop ? "h-[66px] p-5" : "h-14",
                isDesktop && "p-5 pr-16",
                "aria-invalid:border-interactive-danger",
                "group-has-[.text-state-positive]/field:border-state-positive group-has-[.text-state-danger]/field:border-interactive-danger",
                "border-none text-primary-04 focus:outline-none focus-visible:ring-0 p-0 h-auto min-w-[auto] underline pr-[16px] relative right-[-16px] text-right"
              ),
              children: [
                /* @__PURE__ */ jsx("div", { children: selected.firstLine }),
                /* @__PURE__ */ jsx("div", { children: selected.secondLine })
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            "select",
            {
              id,
              ref: setRefs,
              "data-slot": "native-select",
              "aria-invalid": isValid,
              disabled,
              title: `${title}`,
              className: cn(
                isDesktop ? "h-[66px] p-5" : "h-14",
                "focus-within:border-interactive-primary-default-01 focus-within:shadow-[inset_0_0_0_1px_var(--color-border-interactive-primary-default-01)]",
                "min-w-[100px] appearance-none text-basic-02 border rounded-lg border-basic-04 w-full p-4 pr-11 whitespace-nowrap overflow-hidden text-ellipsis body-lg",
                isDesktop && "p-5 pr-16",
                "aria-invalid:border-interactive-danger aria-invalid:!shadow-[inset_0_0_0_1px_var(--color-border-interactive-danger)]",
                "group-has-[.text-state-positive]/field:border-interactive-primary-default-01 !group-has-[.text-state-danger]/field:border-interactive-danger",
                variants === "email" && "border-none focus:outline-none focus-visible:ring-0",
                variants === "text" && "border-none focus:outline-none focus-visible:ring-0 p-0 h-auto min-w-[auto] px-[16px] relative left-[-16px] ",
                variants === "underLine" && "opacity-0 absolute top-0 border-none text-primary-04 focus:outline-none focus-visible:ring-0 p-0 h-auto min-w-[auto] underline pr-[16px] right-[-16px] text-left",
                scale === "small" && "body-md h-[40px] rounded-[8px] !py-[10px] !pl-3 !pr-2",
                disabled && "text-basic-02 border-state-disabled-03",
                isValid && "!border-interactive-danger !shadow-[inset_0_0_0_1px_var(--color-border-interactive-danger)]",
                className
              ),
              onChange: (e) => {
                console.log("####", e.target.value, e.target.selectedOptions[0]?.innerText);
                handUnderLineChange(e);
                onValueChange?.(e.target.value);
                props.onChange?.(e);
              },
              ...props
            }
          ),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "pointer-events-none absolute top-[50%] right-[12px] -translate-y-1/2 select-none flex",
                "md:right-[16px] -mt-[1px]",
                isDesktop && "right-4",
                // variants === 'text' && 'right-0',
                variants === "text" && "right-[4px]",
                variants === "underLine" && "hidden",
                scale === "small" && isDesktop && "right-2",
                "aria-invalid:border-interactive-danger"
              ),
              "aria-hidden": "true",
              "data-slot": "native-select-icon",
              children: /* @__PURE__ */ jsx(
                LineIcon,
                {
                  icoColor: disabled ? "bg-icon-state-disabled" : "bg-icon-basic-01",
                  icoName: "ico-caret-down",
                  className: "ml-auto",
                  type: scale === "small" ? "xs" : "default"
                }
              )
            }
          )
        ]
      }
    );
  }
);
function NativeSelectOption({ ...props }) {
  return /* @__PURE__ */ jsx("option", { "data-slot": "native-select-option", ...props });
}

export { NativeSelect, NativeSelectOption };
